import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Send, Loader2, CheckCircle2 } from 'lucide-react';

interface Message {
  role: string;
  content: string;
  timestamp: string;
}

interface SessionData {
  session_id: string;
  patient_name: string;
  conversation_history: Message[];
}

interface FlaggedInteraction {
  turn_number: number;
  question: string;
  answer: string;
  issue_type: string;
  severity: string;
  comment: string;
}

const PatientFeedbackPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [realism, setRealism] = useState([7]);
  const [educationalValue, setEducationalValue] = useState([7]);
  const [responseAppropriateness, setResponseAppropriateness] = useState([7]);
  const [patientConsistency, setPatientConsistency] = useState([7]);
  const [clinicalAccuracy, setClinicalAccuracy] = useState([7]);

  const [flaggedTurns, setFlaggedTurns] = useState<Set<number>>(new Set());
  const [flaggedInteractions, setFlaggedInteractions] = useState<Map<number, FlaggedInteraction>>(new Map());

  const [strengths, setStrengths] = useState('');
  const [improvements, setImprovements] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');

  const [evaluatorRole, setEvaluatorRole] = useState('');
  const [evaluatorSpecialty, setEvaluatorSpecialty] = useState('');
  const [evaluatorExperience, setEvaluatorExperience] = useState('');

  useEffect(() => {
    fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const response = await apiClient.get(`/api/sessions/${sessionId}`);
      setSession(response.data);
    } catch (error: any) {
      console.error('Failed to fetch session:', error);
      setError(error.response?.data?.detail || 'Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const handleFlagToggle = (turnNumber: number, checked: boolean) => {
    const newFlagged = new Set(flaggedTurns);
    if (!checked) {
      newFlagged.delete(turnNumber);
      const newInteractions = new Map(flaggedInteractions);
      newInteractions.delete(turnNumber);
      setFlaggedInteractions(newInteractions);
    } else {
      newFlagged.add(turnNumber);
    }
    setFlaggedTurns(newFlagged);
  };

  const handleInteractionUpdate = (turnNumber: number, field: string, value: string) => {
    const newInteractions = new Map(flaggedInteractions);
    const existing = newInteractions.get(turnNumber) || {
      turn_number: turnNumber,
      question: session?.conversation_history[turnNumber - 1]?.content || '',
      answer: session?.conversation_history[turnNumber]?.content || '',
      issue_type: 'Unrealistic',
      severity: 'Minor',
      comment: ''
    };
    newInteractions.set(turnNumber, { ...existing, [field]: value });
    setFlaggedInteractions(newInteractions);
  };

  const handleSubmit = async () => {
    if (!strengths && !improvements && flaggedInteractions.size === 0) {
      setError('Please provide at least one form of feedback (ratings, flagged interactions, or qualitative feedback)');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const feedbackData = {
        session_id: sessionId,
        realism: realism[0],
        educational_value: educationalValue[0],
        response_appropriateness: responseAppropriateness[0],
        patient_consistency: patientConsistency[0],
        clinical_accuracy: clinicalAccuracy[0],
        flagged_interactions: Array.from(flaggedInteractions.values()),
        strengths: strengths || null,
        improvements: improvements || null,
        additional_comments: additionalComments || null,
        evaluator_role: evaluatorRole || null,
        evaluator_specialty: evaluatorSpecialty || null,
        evaluator_experience: evaluatorExperience || null
      };

      await apiClient.post(`/api/patient-feedback/submit`, feedbackData);
      setSuccess(true);
    } catch (error: any) {
      console.error('Failed to submit feedback:', error);
      setError(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (success) {
    const averageRating = (realism[0] + educationalValue[0] + responseAppropriateness[0] + patientConsistency[0] + clinicalAccuracy[0]) / 5;

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="bg-primary text-primary-foreground shadow-md">
          <div className="container mx-auto px-4 py-4 flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/patients')}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold">Feedback Submitted</h1>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <Alert className="bg-green-50 border-green-200 mb-6">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-700">
              Thank you! Your feedback has been submitted successfully.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle>Submission Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-primary">{averageRating.toFixed(1)}/10</div>
                  <div className="text-sm text-muted-foreground">Average Rating</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary">{flaggedInteractions.size}</div>
                  <div className="text-sm text-muted-foreground">Flagged Interactions</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary">{strengths || improvements ? 'Yes' : 'No'}</div>
                  <div className="text-sm text-muted-foreground">Qualitative Feedback</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-6 text-center">
                Your feedback directly improves the realism and educational value of this system.
              </p>
            </CardContent>
          </Card>

          <div className="flex justify-center gap-4 mt-6">
            <Button variant="outline" onClick={() => navigate('/patients')}>
              Back to Patients
            </Button>
            <Button onClick={() => navigate(`/evaluation/${sessionId}`)}>
              Back to Evaluation
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="w-full px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/evaluation/${sessionId}`)}
            className="text-primary-foreground hover:bg-primary-foreground/10"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-bold">Evaluate AI Patient Quality</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Quantitative Assessment */}
        <Card>
          <CardHeader>
            <CardTitle>1. Quantitative Assessment</CardTitle>
            <p className="text-sm text-muted-foreground">Rate on 1-10 scale (1=Inadequate, 10=Indistinguishable from human SP)</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <div className="flex justify-between mb-2">
                <Label>Clinical Realism</Label>
                <span className="font-semibold">{realism[0]}</span>
              </div>
              <Slider value={realism} onValueChange={setRealism} min={1} max={10} step={1} />
              <p className="text-xs text-muted-foreground mt-1">Naturalness of language, affect, cognitive patterns</p>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <Label>Educational Utility</Label>
                <span className="font-semibold">{educationalValue[0]}</span>
              </div>
              <Slider value={educationalValue} onValueChange={setEducationalValue} min={1} max={10} step={1} />
              <p className="text-xs text-muted-foreground mt-1">Value for communication training vs. human SP</p>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <Label>Contextual Appropriateness</Label>
                <span className="font-semibold">{responseAppropriateness[0]}</span>
              </div>
              <Slider value={responseAppropriateness} onValueChange={setResponseAppropriateness} min={1} max={10} step={1} />
              <p className="text-xs text-muted-foreground mt-1">Responses matched question intent</p>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <Label>Internal Consistency</Label>
                <span className="font-semibold">{patientConsistency[0]}</span>
              </div>
              <Slider value={patientConsistency} onValueChange={setPatientConsistency} min={1} max={10} step={1} />
              <p className="text-xs text-muted-foreground mt-1">Coherence across multiple responses</p>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <Label>Clinical/Factual Accuracy</Label>
                <span className="font-semibold">{clinicalAccuracy[0]}</span>
              </div>
              <Slider value={clinicalAccuracy} onValueChange={setClinicalAccuracy} min={1} max={10} step={1} />
              <p className="text-xs text-muted-foreground mt-1">Medical correctness of case details</p>
            </div>
          </CardContent>
        </Card>

        {/* Granular Response Evaluation */}
        <Card>
          <CardHeader>
            <CardTitle>2. Granular Response Evaluation</CardTitle>
            <p className="text-sm text-muted-foreground">Flag specific AI responses requiring refinement</p>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="w-full">
              {session?.conversation_history && session.conversation_history.map((msg, index) => {
                if (msg.role !== 'user') return null;
                const turnNumber = Math.floor(index / 2) + 1;
                const patientResponse = session.conversation_history[index + 1];
                if (!patientResponse) return null;

                const isFlagged = flaggedTurns.has(turnNumber);
                const interaction = flaggedInteractions.get(turnNumber);

                return (
                  <AccordionItem key={turnNumber} value={`turn-${turnNumber}`}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={isFlagged}
                          onCheckedChange={(checked) => handleFlagToggle(turnNumber, checked as boolean)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-sm">Turn {turnNumber}: {msg.content.substring(0, 60)}...</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 pt-2">
                        <div>
                          <p className="text-sm font-semibold mb-1">Your Question:</p>
                          <p className="text-sm text-muted-foreground">{msg.content}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold mb-1">Patient Response:</p>
                          <p className="text-sm text-muted-foreground">{patientResponse.content}</p>
                        </div>

                        {isFlagged && (
                          <div className="grid grid-cols-2 gap-4 pt-4">
                            <div>
                              <Label className="text-sm">Issue Type</Label>
                              <Select
                                value={interaction?.issue_type || 'Unrealistic'}
                                onValueChange={(value) => handleInteractionUpdate(turnNumber, 'issue_type', value)}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Unrealistic">Unrealistic</SelectItem>
                                  <SelectItem value="Clinically Inaccurate">Clinically Inaccurate</SelectItem>
                                  <SelectItem value="Inconsistent">Inconsistent</SelectItem>
                                  <SelectItem value="Too Revealing">Too Revealing</SelectItem>
                                  <SelectItem value="Not Revealing Enough">Not Revealing Enough</SelectItem>
                                  <SelectItem value="Inappropriate Tone">Inappropriate Tone</SelectItem>
                                  <SelectItem value="Other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label className="text-sm">Severity</Label>
                              <Select
                                value={interaction?.severity || 'Minor'}
                                onValueChange={(value) => handleInteractionUpdate(turnNumber, 'severity', value)}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Minor">Minor</SelectItem>
                                  <SelectItem value="Moderate">Moderate</SelectItem>
                                  <SelectItem value="Major">Major</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="col-span-2">
                              <Label className="text-sm">What would you change?</Label>
                              <Textarea
                                rows={3}
                                className="mt-1"
                                placeholder="Describe the issue and how the response should be improved..."
                                value={interaction?.comment || ''}
                                onChange={(e) => handleInteractionUpdate(turnNumber, 'comment', e.target.value)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>

        {/* Qualitative Feedback */}
        <Card>
          <CardHeader>
            <CardTitle>3. Qualitative Feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Strengths</Label>
                <Textarea
                  rows={6}
                  className="mt-2"
                  placeholder="What did the AI patient do well? E.g., realistic portrayal of health literacy barriers..."
                  value={strengths}
                  onChange={(e) => setStrengths(e.target.value)}
                />
              </div>
              <div>
                <Label>Improvement Areas</Label>
                <Textarea
                  rows={6}
                  className="mt-2"
                  placeholder="What needs improvement? E.g., responses sometimes too coherent for stated confusion..."
                  value={improvements}
                  onChange={(e) => setImprovements(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label>Additional Comments</Label>
              <Textarea
                rows={4}
                className="mt-2"
                placeholder="Any other observations or suggestions?"
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Evaluator Background */}
        <Card>
          <CardHeader>
            <CardTitle>4. Evaluator Background (Optional)</CardTitle>
            <p className="text-sm text-muted-foreground">Helps contextualize feedback based on clinical experience</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Role</Label>
                <Select value={evaluatorRole} onValueChange={setEvaluatorRole}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Medical Student">Medical Student</SelectItem>
                    <SelectItem value="Resident">Resident</SelectItem>
                    <SelectItem value="Fellow">Fellow</SelectItem>
                    <SelectItem value="Attending">Attending</SelectItem>
                    <SelectItem value="Educator">Educator</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Specialty</Label>
                <Input
                  className="mt-2"
                  placeholder="E.g., Internal Medicine"
                  value={evaluatorSpecialty}
                  onChange={(e) => setEvaluatorSpecialty(e.target.value)}
                />
              </div>

              <div>
                <Label>Years Experience</Label>
                <Select value={evaluatorExperience} onValueChange={setEvaluatorExperience}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0-3">0-3</SelectItem>
                    <SelectItem value="4-7">4-7</SelectItem>
                    <SelectItem value="8-15">8-15</SelectItem>
                    <SelectItem value="15+">15+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-center">
          <Button size="lg" onClick={handleSubmit} disabled={submitting} className="min-w-[200px]">
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Submit Feedback
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PatientFeedbackPage;
